import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { api } from '@/lib/api'
import { formatMoney } from '@/lib/utils'

interface VentasDia { fecha: string; ventas: string; total: string }
interface TopProducto { id: string; nombre: string; marca: string | null; cantidad_vendida: string; total_vendido: string }
interface Ganancias { total_vendido: string; costo_estimado: string; ganancia_estimada: string; ventas_count: string }

type Periodo = 7 | 30 | 90

const PERIODO_LABELS: Record<Periodo, string> = { 7: '7 días', 30: '30 días', 90: '3 meses' }

function useVentasChart(dias: Periodo) {
  return useQuery({
    queryKey: ['dashboard-ventas-chart', dias],
    queryFn: () => api.get<VentasDia[]>(`/api/dashboard/ventas-chart?dias=${dias}`),
    staleTime: 60_000,
  })
}

function useTopProductos(dias: Periodo) {
  return useQuery({
    queryKey: ['dashboard-top-productos', dias],
    queryFn: () => api.get<TopProducto[]>(`/api/dashboard/top-productos?dias=${dias}&limit=8`),
    staleTime: 60_000,
  })
}

function useGanancias(dias: Periodo) {
  return useQuery({
    queryKey: ['dashboard-ganancias', dias],
    queryFn: () => api.get<Ganancias>(`/api/dashboard/ganancias?dias=${dias}`),
    staleTime: 60_000,
  })
}

const ACID = '#c9f000'
const ACID_INK = '#3a4800'
const GRID_COLOR = 'rgba(26,25,14,0.07)'

function formatFecha(iso: string) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
}

function PeriodoPicker({ value, onChange }: { value: Periodo; onChange: (p: Periodo) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {([7, 30, 90] as Periodo[]).map(p => (
        <button key={p} onClick={() => onChange(p)}
          style={{
            padding: '4px 12px', borderRadius: 6, border: '1px solid var(--me-line)',
            background: value === p ? 'var(--me-acid)' : 'var(--me-surface)',
            color: value === p ? 'var(--me-acid-ink)' : 'var(--me-text-3)',
            fontFamily: 'var(--me-sans)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
            transition: 'background var(--me-dur-short)',
          }}>
          {PERIODO_LABELS[p]}
        </button>
      ))}
    </div>
  )
}

const TooltipVentas = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: 'var(--me-surface)', border: '1px solid var(--me-line)', borderRadius: 10, padding: '10px 14px', boxShadow: 'var(--me-shadow-card)' }}>
      <div style={{ fontSize: 11, color: 'var(--me-text-4)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--me-mono)', fontWeight: 700, fontSize: 15, color: 'var(--me-text)' }}>
        {formatMoney(payload[0]?.value ?? 0)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--me-text-3)' }}>{payload[1]?.value ?? 0} ventas</div>
    </div>
  )
}

const TooltipProductos = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: 'var(--me-surface)', border: '1px solid var(--me-line)', borderRadius: 10, padding: '10px 14px', boxShadow: 'var(--me-shadow-card)' }}>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: 'var(--me-text)' }}>{payload[0]?.payload?.nombre}</div>
      <div style={{ fontFamily: 'var(--me-mono)', fontWeight: 700, fontSize: 14, color: ACID_INK }}>
        {Number(payload[0]?.value).toLocaleString('es-AR')} unidades
      </div>
    </div>
  )
}

export default function AdminCharts() {
  const [periodo, setPeriodo] = useState<Periodo>(30)

  const { data: ventasDia = [], isLoading: loadingVentas } = useVentasChart(periodo)
  const { data: topProductos = [], isLoading: loadingTop } = useTopProductos(periodo)
  const { data: ganancias, isLoading: loadingGanancias } = useGanancias(periodo)

  const chartVentas = ventasDia.map(d => ({
    fecha: formatFecha(d.fecha),
    total: parseFloat(d.total),
    ventas: parseInt(d.ventas),
  }))

  const chartTop = topProductos.map(p => ({
    nombre: p.nombre.length > 18 ? p.nombre.slice(0, 16) + '…' : p.nombre,
    nombreFull: p.nombre,
    cantidad: parseFloat(p.cantidad_vendida),
    totalVendido: parseFloat(p.total_vendido),
  }))

  const totalVendido = parseFloat(ganancias?.total_vendido ?? '0')
  const costoEstimado = parseFloat(ganancias?.costo_estimado ?? '0')
  const gananciaEst = parseFloat(ganancias?.ganancia_estimada ?? '0')
  const margen = totalVendido > 0 ? (gananciaEst / totalVendido) * 100 : 0

  return (
    <div style={{ marginTop: 32 }}>
      {/* Separador */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div style={{ height: 1, flex: 1, background: 'var(--me-line)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--me-text-4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Análisis admin</span>
          <PeriodoPicker value={periodo} onChange={setPeriodo} />
        </div>
        <div style={{ height: 1, flex: 1, background: 'var(--me-line)' }} />
      </div>

      {/* KPIs de ganancias */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="Total vendido" value={formatMoney(totalVendido)} loading={loadingGanancias} />
        <KpiCard label="Costo estimado" value={formatMoney(costoEstimado)} loading={loadingGanancias} color="var(--me-text-3)" />
        <KpiCard
          label="Ganancia estimada"
          value={formatMoney(gananciaEst)}
          loading={loadingGanancias}
          color={gananciaEst >= 0 ? 'var(--me-ok)' : 'var(--me-bad)'}
          sub={`Margen ${margen.toFixed(1)}%`}
        />
        <KpiCard label="Ventas del período" value={ganancias?.ventas_count ?? '0'} loading={loadingGanancias} mono />
      </div>

      {/* Gráficas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>

        {/* Línea de ventas */}
        <div className="me-card" style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--me-text-2)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Ventas por día
          </div>
          {loadingVentas || chartVentas.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--me-text-4)', fontSize: 12 }}>
              {loadingVentas ? 'Cargando...' : 'Sin datos en el período'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartVentas} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradVentas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ACID} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={ACID} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: 'var(--me-text-4)' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--me-text-4)' }} tickLine={false} axisLine={false}
                  tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip content={<TooltipVentas />} />
                <Area type="monotone" dataKey="total" stroke={ACID} strokeWidth={2.5}
                  fill="url(#gradVentas)" dot={false} activeDot={{ r: 4, fill: ACID, strokeWidth: 0 }} />
                <Area type="monotone" dataKey="ventas" stroke="transparent" fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top productos */}
        <div className="me-card" style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--me-text-2)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Top productos vendidos
          </div>
          {loadingTop || chartTop.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--me-text-4)', fontSize: 12 }}>
              {loadingTop ? 'Cargando...' : 'Sin ventas en el período'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartTop} layout="vertical" margin={{ top: 0, right: 4, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--me-text-4)' }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="nombre" width={80}
                  tick={{ fontSize: 10, fill: 'var(--me-text-3)' }} tickLine={false} axisLine={false} />
                <Tooltip content={<TooltipProductos />} cursor={{ fill: 'var(--me-surface-2)' }} />
                <Bar dataKey="cantidad" fill={ACID} radius={[0, 4, 4, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}

function KpiCard({ label, value, loading, color, sub, mono }: {
  label: string; value: string; loading?: boolean; color?: string; sub?: string; mono?: boolean
}) {
  return (
    <div className="me-card">
      <div style={{ fontSize: 11, color: 'var(--me-text-4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      {loading ? (
        <div style={{ height: 28, background: 'var(--me-surface-3)', borderRadius: 6, width: '70%' }} />
      ) : (
        <>
          <div style={{
            fontFamily: mono ? 'var(--me-mono)' : 'var(--me-display)',
            fontVariationSettings: mono ? undefined : '"opsz" 24, "wght" 700',
            fontFeatureSettings: mono ? '"tnum"' : undefined,
            fontWeight: 700, fontSize: 20,
            color: color ?? 'var(--me-text)',
            letterSpacing: '-0.02em',
          }}>
            {value}
          </div>
          {sub && <div style={{ fontSize: 11, color: 'var(--me-text-4)', marginTop: 2 }}>{sub}</div>}
        </>
      )}
    </div>
  )
}
